"use client";

import Link from "@/components/atlas-link";
import { ChevronDown, SquarePen } from "lucide-react";
import {
  adminNavGroups,
  adminNavItems,
  findActiveAdminNavItem,
} from "@/components/admin/admin-nav";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

// CSS keeps the motion runtime out of the shared portal shell bundle.
const ITEM_CLASS =
  "transition-[transform,opacity] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none";

function itemMotion(active: boolean, index: number) {
  return {
    className: cn(ITEM_CLASS, !active && "translate-y-3 opacity-0"),
    style: { transitionDelay: active ? `${50 + index * 35}ms` : "0ms" },
  };
}

type PortalAdminSidebarNavProps = {
  active?: boolean;
  className?: string;
  contentCmsHref?: string;
  pathname: string | null;
  /** Server-derived: the current admin also carries the "developer" tag. */
  showDeveloperItems?: boolean;
};

export function PortalAdminSidebarNav({
  active = true,
  className,
  contentCmsHref,
  pathname,
  showDeveloperItems = false,
}: PortalAdminSidebarNavProps) {
  const activeItem = findActiveAdminNavItem(pathname ?? "");
  const navItems = adminNavItems.filter(
    (item) => !item.external && (!item.developerOnly || showDeveloperItems),
  );

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col p-2", className)}>
      <SidebarGroup className="p-0">
        <SidebarGroupLabel asChild>
          <Link
            className="flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground"
            href="/dashboard"
          >
            <span
              className={cn(
                "inline-flex shrink-0 transition-transform duration-300 motion-reduce:transition-none",
                !active && "-rotate-90",
              )}
            >
              <ChevronDown className="size-4" />
            </span>
            <span>Admin</span>
          </Link>
        </SidebarGroupLabel>
        <SidebarGroupContent className="flex flex-col gap-3">
          {adminNavGroups.map((group) => {
            const groupItems = navItems.filter((item) => item.group === group.key);
            if (groupItems.length === 0) return null;
            return (
              <div className="flex flex-col gap-1" key={group.key}>
                <p className="px-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-sidebar-foreground/50 group-data-[collapsible=icon]:sr-only">
                  {group.label}
                </p>
                <SidebarMenu>
                  {groupItems.map((item) => {
                    const Icon = item.icon;
                    const index = navItems.indexOf(item);
                    return (
                      <div key={item.href} {...itemMotion(active, index)}>
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            asChild
                            isActive={item.href === activeItem?.href}
                            tooltip={item.label}
                          >
                            <Link href={item.href}>
                              <Icon />
                              <span>{item.label}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      </div>
                    );
                  })}
                </SidebarMenu>
              </div>
            );
          })}
          {contentCmsHref ? (
            <div className="flex flex-col gap-1">
              <p className="px-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-sidebar-foreground/50 group-data-[collapsible=icon]:sr-only">
                Publishing
              </p>
              <SidebarMenu>
              <div {...itemMotion(active, navItems.length)}>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Content CMS">
                    <a
                      href={contentCmsHref}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <SquarePen />
                      <span>Content CMS</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </div>
              </SidebarMenu>
            </div>
          ) : null}
        </SidebarGroupContent>
      </SidebarGroup>
    </div>
  );
}
